import { type AppBarProps, Link, Stack, Typography, useMediaQuery } from '@mui/material';
import ChatBotIcon from 'assets/icons/chat-bot.svg';
import { META } from 'common/constants';
import { TertiaryButton } from 'components/lib/TertiaryButton';
import { SvgIconWrapper } from 'components/SvgIconWrapper';
import { useFrontChat } from 'front-chat-sdk/hooks';
import theme from 'providers/theme/theme';
import { type FC, useEffect, useState } from 'react';
import { useChatWidget } from 'store/features/chatWidget';
import { FooterLinkStyle, FooterStyle } from './Footer.styles';

type FooterProps = {
  displayPhoneNumber?: boolean;
  displayChatWidget?: boolean;
} & AppBarProps;

const Footer: FC<FooterProps> = ({ displayPhoneNumber = true, displayChatWidget = false }) => {
  const { frontChat } = useFrontChat();
  const { isChatWidgetWindowVisible, isInitialized } = useChatWidget();

  const isMobileLayout = useMediaQuery(theme.breakpoints.down('sm'));

  const isDesktopLayout = useMediaQuery(theme.breakpoints.up('md'));

  const [disableCallUsLink, setDisableCallUsLink] = useState(false);

  const handleOpenChatWidget = () => {
    frontChat?.('show');
  };

  useEffect(() => {
    if (!displayChatWidget || isDesktopLayout) {
      return undefined;
    }

    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    if (isChatWidgetWindowVisible) {
      setDisableCallUsLink(true);
    } else {
      timeoutId = setTimeout(() => {
        setDisableCallUsLink(false);
      }, 1000); // slight delay to prevent flicker when closing chat and returning to footer
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [displayChatWidget, isChatWidgetWindowVisible, isDesktopLayout]);

  const displayChatWidgetButton = displayChatWidget && !isDesktopLayout && isInitialized;

  return (
    <Stack
      sx={{
        width: '100%',
        height: (theme) => theme.customStyling.footer.height,
        flexDirection: 'row',
        boxShadow: (theme) => theme.customStyling.shadow.footer,
        px: 3,
        gap: 1,
      }}
    >
      {displayChatWidgetButton && (
        <TertiaryButton
          sx={[
            FooterLinkStyle,
            {
              padding: 0,
              width: 'unset',
              flexDirection: isMobileLayout ? 'column' : 'row',
              color: 'white',
              minWidth: 'unset',
              '&:hover': {
                color: theme.palette.customColors.cream,
              },
            },
          ]}
          startAdornment={<SvgIconWrapper src={ChatBotIcon} />}
          onClick={handleOpenChatWidget}
          text={
            <Typography variant="body2" sx={FooterLinkStyle} whiteSpace="nowrap">
              Chat
            </Typography>
          }
        />
      )}

      {displayPhoneNumber && (
        <Stack sx={FooterStyle}>
          <Link
            tabIndex={-1}
            variant="body2"
            sx={{
              ...FooterLinkStyle,
              flexDirection: isMobileLayout ? 'column' : 'row',
              pointerEvents: disableCallUsLink ? 'none' : 'auto',
            }}
            href={`tel:${META.CONTACT_NUMBER}`}
            target="_blank"
          >
            <Typography
              variant="body2"
              whiteSpace="nowrap"
              fontWeight={600}
              sx={{ color: (theme) => theme.palette.customColors.gunMetal }}
            >
              Need Assistance? Give Us a Call
            </Typography>

            <Typography
              whiteSpace="nowrap"
              sx={{
                fontWeight: 800,
                color: (theme) => theme.palette.customColors.darkCopper,
                textDecoration: 'underline dotted',
              }}
            >
              {META.CONTACT_NUMBER}
            </Typography>
          </Link>

          <Typography
            variant="body2"
            sx={{ color: (theme) => theme.palette.customColors.darkGray }}
          >
            Mon. - Fri. 8am-9pm EST
          </Typography>
        </Stack>
      )}
    </Stack>
  );
};

export default Footer;
